import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { errMsg, fmtDate, fmtMoney } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Empty, StatusBadge } from "../components/common";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Inbox as InboxIcon, Bell, FileText, Receipt, Wrench, ShoppingCart, ScrollText,
  CheckCircle2, Trash2, ArrowRight, Circle, MailOpen, ShieldCheck,
} from "lucide-react";

const TAB_LIST = [
  { key: "pending", label: "Pendientes", icon: InboxIcon },
  { key: "notifications", label: "Notificaciones", icon: Bell },
];

export default function Inbox() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending");
  const [inbox, setInbox] = useState(null);
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inboxRes, notifRes] = await Promise.all([
        api.get("/my/inbox"),
        api.get("/notifications"),
      ]);
      setInbox(inboxRes.data);
      setNotifs(notifRes.data);
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      toast.success("Todo marcado como leído");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const deleteNotif = async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (loading || !inbox) return <div className="text-xs text-muted-foreground">{t("common.loading")}</div>;

  const totals = inbox.totals;
  const sections = [
    { key: "vales", label: "Vales de Salida a Autorizar", icon: Receipt, items: inbox.vales, color: "text-primary" },
    { key: "requisitions", label: "Requisiciones a Aprobar", icon: FileText, items: inbox.requisitions, color: "text-blue-600" },
    { key: "work_orders", label: "Órdenes de Trabajo Asignadas", short: "OT asignadas", icon: Wrench, items: inbox.work_orders, color: "text-orange-600" },
    { key: "po_approvals", label: "Órdenes de Compra por Autorizar", short: "OC por firmar", icon: ShieldCheck, items: inbox.po_approvals || [], color: "text-red-600" },
    { key: "purchase_orders", label: "Órdenes de Compra en Curso", short: "OC en curso", icon: ShoppingCart, items: inbox.purchase_orders, color: "text-emerald-600" },
    { key: "invoices", label: "Facturas para Conciliar", icon: ScrollText, items: inbox.invoices, color: "text-violet-600" },
  ];
  const visibleSections = filter === "all" ? sections : sections.filter((s) => s.key === filter);

  return (
    <div data-testid="inbox-page">
      <PageHeader title="Mi Escritorio" subtitle={`Todo lo que requiere tu atención, ${user?.name || user?.email}`} />

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-6">
        <button onClick={() => { setTab("pending"); setFilter("all"); }}
                className={`border p-3 text-left transition-colors ${tab === "pending" && filter === "all" ? "bg-primary/5 border-primary" : "hover:bg-muted"}`}
                data-testid="inbox-total-card">
          <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Total pendiente</div>
          <div className="text-2xl font-black text-primary">{totals.total}</div>
        </button>
        {sections.map((s) => (
          <button key={s.key} onClick={() => { setTab("pending"); setFilter(s.key); }}
                  className={`border p-3 text-left transition-colors ${filter === s.key ? "bg-primary/5 border-primary" : "hover:bg-muted"}`}
                  data-testid={`inbox-kpi-${s.key}`}>
            <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest text-muted-foreground">
              <s.icon size={11} /> {s.short || s.label.split(" ")[0]}
            </div>
            <div className={`text-xl font-black ${s.color}`}>{s.items.length}</div>
          </button>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex border-b mb-4">
        {TAB_LIST.map((tb) => (
          <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`inbox-tab-${tb.key}`}
                  className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border-b-2 -mb-px transition-colors flex items-center gap-2 ${
                    tab === tb.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <tb.icon size={12} /> {tb.label}
            {tb.key === "notifications" && notifs.unread > 0 && (
              <span className="bg-destructive text-destructive-foreground text-[9px] font-bold rounded-full px-1.5 py-0.5">{notifs.unread}</span>
            )}
          </button>
        ))}
      </div>

      {/* PENDING TAB */}
      {tab === "pending" && (
        <div className="space-y-6">
          {visibleSections.every((s) => s.items.length === 0) && (
            <Empty text="No tienes pendientes 🎉" />
          )}
          {visibleSections.filter((s) => s.items.length > 0).map((s) => (
            <section key={s.key} data-testid={`inbox-section-${s.key}`}>
              <div className={`text-xs font-bold uppercase tracking-widest ${s.color} flex items-center gap-2 mb-2`}>
                <s.icon size={14} /> {s.label} ({s.items.length})
              </div>
              <div className="border">
                <table className="w-full text-xs">
                  <thead className="bg-muted">
                    <tr>
                      {s.key === "vales" && <>
                        <th className="p-2 text-left">Folio</th>
                        <th className="p-2 text-left">Solicitante</th>
                        <th className="p-2 text-left">Depto</th>
                        <th className="p-2 text-left">Generador</th>
                        <th className="p-2 text-right">Partidas</th>
                      </>}
                      {s.key === "requisitions" && <>
                        <th className="p-2 text-left">Folio</th>
                        <th className="p-2 text-left">Solicitante</th>
                        <th className="p-2 text-right">Total</th>
                        <th className="p-2 text-right">Partidas</th>
                        <th className="p-2 text-left">Estatus</th>
                      </>}
                      {s.key === "work_orders" && <>
                        <th className="p-2 text-left">Folio</th>
                        <th className="p-2 text-left">Equipo</th>
                        <th className="p-2 text-left">Tipo</th>
                        <th className="p-2 text-left">Prioridad</th>
                        <th className="p-2 text-left">Estatus</th>
                      </>}
                      {s.key === "po_approvals" && <>
                        <th className="p-2 text-left">Folio</th>
                        <th className="p-2 text-left">Nivel</th>
                        <th className="p-2 text-left">Proveedor</th>
                        <th className="p-2 text-left">Depto / Req.</th>
                        <th className="p-2 text-right">Total</th>
                      </>}
                      {s.key === "purchase_orders" && <>
                        <th className="p-2 text-left">Folio</th>
                        <th className="p-2 text-left">Proveedor</th>
                        <th className="p-2 text-right">Total</th>
                        <th className="p-2 text-left">Estatus</th>
                      </>}
                      {s.key === "invoices" && <>
                        <th className="p-2 text-left">Folio</th>
                        <th className="p-2 text-left">Proveedor</th>
                        <th className="p-2 text-right">Monto</th>
                      </>}
                      <th className="p-2 text-left">Fecha</th>
                      <th className="p-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {s.items.map((it) => (
                      <tr key={it.id} className="border-t hover:bg-muted/50 cursor-pointer"
                          onClick={() => navigate(it.link)}
                          data-testid={`inbox-item-${s.key}-${it.id}`}>
                        {s.key === "vales" && <>
                          <td className="p-2 font-bold">{it.number}</td>
                          <td className="p-2">{it.requester || "—"}</td>
                          <td className="p-2">{it.department}</td>
                          <td className="p-2 text-muted-foreground">{it.generator}</td>
                          <td className="p-2 text-right">{it.lines_count}</td>
                        </>}
                        {s.key === "requisitions" && <>
                          <td className="p-2 font-bold">{it.number}</td>
                          <td className="p-2 text-muted-foreground">{it.requester}</td>
                          <td className="p-2 text-right tabular-nums">{fmtMoney(it.total)} {it.currency}</td>
                          <td className="p-2 text-right">{it.lines_count}</td>
                          <td className="p-2">
                            {it.can_approve
                              ? <span className="text-[9px] font-bold uppercase text-primary">Puedes aprobar</span>
                              : <span className="text-[9px] font-bold uppercase text-muted-foreground">Excede límite</span>}
                          </td>
                        </>}
                        {s.key === "work_orders" && <>
                          <td className="p-2 font-bold">{it.number}</td>
                          <td className="p-2">{it.equipment}</td>
                          <td className="p-2">{it.type}</td>
                          <td className="p-2 uppercase text-[10px] font-bold">{it.priority}</td>
                          <td className="p-2"><StatusBadge status={it.status} /></td>
                        </>}
                        {s.key === "po_approvals" && <>
                          <td className="p-2 font-bold">{it.number}</td>
                          <td className="p-2">
                            <span className={`text-[9px] font-bold uppercase border px-1.5 py-0.5 ${it.level === 1 ? "border-amber-300 bg-amber-50 text-amber-800" : "border-orange-300 bg-orange-50 text-orange-800"}`} data-testid={`inbox-po-level-${it.number}`}>
                              {it.level === 1 ? "1 · Encargado depto" : "2 · Autorización final"}
                            </span>
                            {!it.mine && <span className="ml-1 text-[9px] text-muted-foreground uppercase">(admin)</span>}
                          </td>
                          <td className="p-2">{it.supplier}</td>
                          <td className="p-2 text-[11px]">{it.department || "—"}{it.requisition && <span className="text-muted-foreground"> · {it.requisition}</span>}</td>
                          <td className="p-2 text-right tabular-nums">{fmtMoney(it.total)} {it.currency}</td>
                        </>}
                        {s.key === "purchase_orders" && <>
                          <td className="p-2 font-bold">{it.number}</td>
                          <td className="p-2">{it.supplier}</td>
                          <td className="p-2 text-right tabular-nums">{fmtMoney(it.total)} {it.currency}</td>
                          <td className="p-2"><StatusBadge status={it.status} /></td>
                        </>}
                        {s.key === "invoices" && <>
                          <td className="p-2 font-bold">{it.number}</td>
                          <td className="p-2">{it.supplier}</td>
                          <td className="p-2 text-right tabular-nums">{fmtMoney(it.amount)} {it.currency}</td>
                        </>}
                        <td className="p-2 text-muted-foreground text-[11px]">{fmtDate(it.created_at)}</td>
                        <td className="p-2 text-right"><ArrowRight size={12} className="text-muted-foreground" /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>
      )}

      {/* NOTIFICATIONS TAB */}
      {tab === "notifications" && (
        <div>
          <div className="flex justify-between items-center mb-3">
            <div className="text-xs text-muted-foreground">
              {notifs.unread} sin leer · {notifs.items.length} en total
            </div>
            {notifs.unread > 0 && (
              <Button size="sm" variant="outline" className="rounded-none text-xs font-bold"
                      onClick={markAllRead} data-testid="notif-mark-all-read">
                <MailOpen size={12} className="mr-1" /> Marcar todo como leído
              </Button>
            )}
          </div>
          {notifs.items.length === 0 ? (
            <Empty text="Sin notificaciones" />
          ) : (
            <ul className="space-y-1" data-testid="notif-list">
              {notifs.items.map((n) => (
                <li key={n.id}
                    className={`border p-3 flex gap-3 items-start transition-colors ${n.read ? "bg-card" : "bg-primary/5 border-primary/30"}`}
                    data-testid={`notif-${n.id}`}>
                  <div className="mt-0.5">
                    {n.read ? <Circle size={10} className="text-muted-foreground" /> : <Circle size={10} className="fill-primary text-primary" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className={`text-sm ${n.read ? "" : "font-bold"}`}>{n.title}</div>
                      {n.doc_number && (
                        <span className="text-[9px] font-bold uppercase bg-muted px-1.5 py-0.5">{n.doc_type} · {n.doc_number}</span>
                      )}
                    </div>
                    {n.body && <div className="text-xs text-muted-foreground whitespace-pre-line mt-1">{n.body}</div>}
                    <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-2">
                      <span>{fmtDate(n.created_at)}</span>
                      {n.actor_email && <span>· por {n.actor_email}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    {n.link && (
                      <Button size="sm" variant="ghost" className="h-7 px-2 rounded-none"
                              onClick={() => { if (!n.read) markRead(n.id); navigate(n.link); }}
                              data-testid={`notif-open-${n.id}`}>
                        <ArrowRight size={12} />
                      </Button>
                    )}
                    {!n.read && (
                      <Button size="sm" variant="ghost" className="h-7 px-2 rounded-none"
                              onClick={() => markRead(n.id)} data-testid={`notif-read-${n.id}`}>
                        <CheckCircle2 size={12} />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-7 px-2 rounded-none text-destructive"
                            onClick={() => deleteNotif(n.id)} data-testid={`notif-delete-${n.id}`}>
                      <Trash2 size={12} />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
